import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { openModal } from "lib/actions/modal";
import { getAIImportStatus } from "lib/aiImport";
import Modals from "lib/constants/Modals";
import { TAsset } from "types";

export const AI_IMPORT_DISABLED_REASON = "AI certificate import isn't enabled — ask an administrator";

type CalibrationCertificateImport = {
  /** Whether the tenant has an AI import key configured. */
  enabled: boolean;
  /** Empty while enabled, so it can be spread onto a `title` without a conditional. */
  disabledReason: string;
  openImport: (asset: TAsset, onSaved: () => void) => void;
};

/**
 * Wires a call site up to the AI certificate import.
 *
 * The API key is tenant configuration rather than a build-time setting, so whether
 * the action can succeed has to be asked of the API. Both the answer and the copy
 * explaining a "no" live here so the two pages offering the action stay consistent.
 */
export const useCalibrationCertificateImport = (): CalibrationCertificateImport => {
  const dispatch = useDispatch();
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getAIImportStatus()
      .then((status) => {
        if (!cancelled) setEnabled(status.enabled);
      })
      // A failed status lookup leaves the feature off rather than offering an
      // action that cannot succeed.
      .catch(() => {
        if (!cancelled) setEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Each page reloads different data once a certificate lands, so the refresh is the
  // caller's to supply.
  const openImport = (asset: TAsset, onSaved: () => void) =>
    dispatch(openModal(Modals.IMPORT_CALIBRATION_CERTIFICATE, { asset, onSaved }));

  return { enabled, disabledReason: enabled ? "" : AI_IMPORT_DISABLED_REASON, openImport };
};
